import React, { useEffect, Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { InstallPrompt } from "@/components/InstallPrompt";
import { AdminRoute } from "@/components/AdminRoute";
import { notificationService } from "@/services/notificationService";
import { medicationNotificationService } from "@/services/medicationNotificationService";

// Lazy load route components for better code splitting
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Admin = lazy(() => import("./pages/Admin"));
const MedicationForm = lazy(() => import("./pages/MedicationForm"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => {
  useEffect(() => {
    // Initialize PWA notifications
    const initNotifications = async () => {
      console.log('🔔 Inicializando notificações PWA');
      
      // Request permission after 2 seconds
      const timer = setTimeout(async () => {
        if (notificationService.getPermissionStatus() === "default") {
          const result = await notificationService.requestPermission();
          if (result.granted) {
            // Sync medications on permission granted
            await notificationService.syncAllMedications();
            
            // Re-sync every hour
            setInterval(() => {
              notificationService.syncAllMedications();
            }, 60 * 60 * 1000);
          }
        } else if (notificationService.getPermissionStatus() === "granted") {
          // Already granted, sync immediately
          await notificationService.syncAllMedications();
          
          // Re-sync every hour
          setInterval(() => {
            notificationService.syncAllMedications();
          }, 60 * 60 * 1000);
        }
      }, 2000);
      
      return () => clearTimeout(timer);
    };

    initNotifications();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Carregando...</div>}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
              <Route path="/medications/new" element={<MedicationForm />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <InstallPrompt />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
